// The phrase-replay matcher on the web client: the fold and the audio rule.
//
// A tagged tutor message carries two lists. `writtenSpans` holds the runs the
// model tagged in the text the learner reads, each {lang, text, start, end}.
// `spanTimings` holds the runs of the reply's audio, each {lang?, text, start,
// end, onset}, with `lang` left out for a run the model did not tag. This
// module decides which spoken run, if any, supplies the audio for each written
// run, which is what makes an underlined phrase playable.
//
// The server makes the same decision in functions/source/helpers/phraseReplay.py
// to count matched phrases, so the two implementations must give exactly the
// same answers. Both assert the same two fixture files,
// tests/fixtures/phraseReplayFold.json and
// tests/fixtures/phraseReplayAudioRule.json, whose `description` lists spell the
// rules out in full. Lengths and positions here are counted in Unicode code
// points, as Python counts them, never in UTF-16 code units.
//
// Spec: docs/superpowers/specs/2026-09-25-audio-playback-speed-and-phrase-replay-design.md,
// section 3.

// Exactly the set JavaScript's `\s` matches, spelled out because the Python
// side spells out the same set (its own `\s` differs).
const WHITESPACE_CHARACTERS =
  "\\t\\n\\u000B\\u000C\\r \\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF";
const WHITESPACE_RUN_PATTERN = new RegExp(`[${WHITESPACE_CHARACTERS}]+`, "g");
// The nine quote marks, each of which becomes the apostrophe U+0027.
const QUOTE_PATTERN = /[\u2018\u2019\u02BC\u201C\u201D\u00AB\u00BB'"]/g;
// A fill-in-the-blank placeholder: an underscore, any run of underscores and
// whitespace, then an underscore. cleanForTts speaks it as an ellipsis.
const BLANK_PLACEHOLDER_PATTERN = new RegExp(`_[_${WHITESPACE_CHARACTERS}]*_`, "g");
// A maximal run of full stops and horizontal ellipses. It becomes "..." when
// it is two or more characters long or holds an ellipsis; a single full stop
// is left alone.
const DOT_RUN_PATTERN = /[.\u2026]+/g;
const REPEATED_PUNCTUATION_PATTERN = /\?{2,}|!{2,}|,{2,}/g;
const DASH_PATTERN = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;
const SYMBOL_SPELLINGS = Object.freeze({
  "\u00B1": "+-",
  "\u00A9": "(c)",
  "\u00AE": "(r)",
  "\u2122": "(tm)",
});
const SYMBOL_PATTERN = /[\u00B1\u00A9\u00AE\u2122]/g;
const LETTER_MARK_OR_NUMBER = /^[\p{L}\p{M}\p{N}]$/u;
const LETTER = /^\p{L}$/u;

// Learning languages written without spaces between words; containment there
// needs four code points and no word boundary. Korean is grouped with them, as
// the Python side and the experiment that measured the rule both do.
export const UNSPACED_LANGUAGE_CODES = Object.freeze(["zh", "ja", "ko"]);
const MIN_CONTAINED_LETTERS = 3;
const MIN_CONTAINED_UNSPACED_CHARACTERS = 4;

export const MATCH_EXACT = "exact";
export const MATCH_CONTAINED = "contained";
export const MATCH_NONE = "none";

function isLetterMarkOrNumber(character) {
  return LETTER_MARK_OR_NUMBER.test(character);
}

// The audio rule's word character: a letter, mark or number, or an
// apostrophe, so `est` does not match inside `c'est`.
function isWordCharacter(character) {
  return isLetterMarkOrNumber(character) || character === "'" || character === "\u2019";
}

/**
 * The comparison form of a phrase, applied to both sides of every comparison.
 * The steps and their order are the fold fixture's `description` list.
 * Anything that is not a string folds to the empty string, which never
 * matches.
 * @param {*} text
 * @returns {string}
 */
export function foldPhrase(text) {
  if (typeof text !== "string") return "";
  let folded = text.replace(QUOTE_PATTERN, "'");
  folded = folded.replace(BLANK_PLACEHOLDER_PATTERN, "\u2026");
  folded = folded.replace(DOT_RUN_PATTERN, (run) =>
    run.length >= 2 || run.includes("\u2026") ? "..." : run
  );
  folded = folded.replace(REPEATED_PUNCTUATION_PATTERN, (run) => run[0]);
  folded = folded.replace(DASH_PATTERN, "-");
  folded = folded.replace(SYMBOL_PATTERN, (symbol) => SYMBOL_SPELLINGS[symbol]);
  folded = folded.normalize("NFKC");
  folded = folded.toLowerCase();
  folded = folded.replace(WHITESPACE_RUN_PATTERN, " ");
  const characters = Array.from(folded);
  let start = 0;
  let end = characters.length;
  while (start < end && !isLetterMarkOrNumber(characters[start])) start++;
  while (end > start && !isLetterMarkOrNumber(characters[end - 1])) end--;
  return characters.slice(start, end).join("");
}

/**
 * The base ISO 639-1 code of a learning language ("es" for "es-MX"), or null.
 * @param {*} learningLang
 * @returns {string|null}
 */
export function learningLanguageCode(learningLang) {
  if (typeof learningLang !== "string") return null;
  const code = learningLang.split("-")[0].split("_")[0].trim().toLowerCase();
  return code || null;
}

function spanLang(span) {
  const lang = span && typeof span === "object" ? span.lang : null;
  return typeof lang === "string" ? lang.toLowerCase() : null;
}

function spanText(span) {
  const text = span && typeof span === "object" ? span.text : null;
  return typeof text === "string" ? text : null;
}

// For each entry, its folded text when it is a well-formed span in the
// learning language, and null otherwise, aligned with the list given.
function learningFolds(spans, code) {
  if (!Array.isArray(spans)) return [];
  return spans.map((span) => {
    const text = spanText(span);
    if (code === null || text === null || spanLang(span) !== code) return null;
    return foldPhrase(text);
  });
}

function codePointLength(text) {
  return Array.from(text).length;
}

// The code point that ends just before UTF-16 index `index`, or "".
function codePointBefore(text, index) {
  if (index <= 0) return "";
  const low = text.charCodeAt(index - 1);
  if (index >= 2 && low >= 0xdc00 && low <= 0xdfff) {
    const high = text.charCodeAt(index - 2);
    if (high >= 0xd800 && high <= 0xdbff) return text.slice(index - 2, index);
  }
  return text[index - 1];
}

// The code point that starts at UTF-16 index `index`, or "".
function codePointAtIndex(text, index) {
  if (index >= text.length) return "";
  return String.fromCodePoint(text.codePointAt(index));
}

// Whether some occurrence of `needle` in `haystack`, overlapping ones
// included, starts and ends on a boundary between a word character and
// something else (or the edge of the text).
function containsOnWordBoundaries(needle, haystack) {
  let position = haystack.indexOf(needle);
  while (position !== -1) {
    const end = position + needle.length;
    const before = codePointBefore(haystack, position);
    const after = codePointAtIndex(haystack, end);
    const startsOnBoundary = before === "" || !isWordCharacter(before);
    const endsOnBoundary = after === "" || !isWordCharacter(after);
    if (startsOnBoundary && endsOnBoundary) return true;
    position = haystack.indexOf(needle, position + 1);
  }
  return false;
}

function mayBeContained(folded, unspaced) {
  if (unspaced) return codePointLength(folded) >= MIN_CONTAINED_UNSPACED_CHARACTERS;
  let letters = 0;
  for (const character of folded) {
    if (LETTER.test(character)) letters++;
  }
  return letters >= MIN_CONTAINED_LETTERS;
}

// [kind, group] for one folded written span: the kind of match and the
// indices of the spoken spans that are the same spoken text, in reply order.
function matchGroup(folded, spokenFolds, unspaced) {
  const exact = [];
  spokenFolds.forEach((spoken, index) => {
    if (spoken === folded) exact.push(index);
  });
  if (exact.length) return [MATCH_EXACT, exact];
  if (!mayBeContained(folded, unspaced)) return [MATCH_NONE, []];
  const containing = [];
  spokenFolds.forEach((spoken, index) => {
    if (!spoken) return;
    const contains = unspaced
      ? spoken.includes(folded)
      : containsOnWordBoundaries(folded, spoken);
    if (contains) containing.push(index);
  });
  if (!containing.length) return [MATCH_NONE, []];
  // The shortest containing span is the sentence the learner read the phrase
  // in; among equal lengths the earliest wins.
  let winner = containing[0];
  for (const index of containing) {
    if (codePointLength(spokenFolds[index]) < codePointLength(spokenFolds[winner])) {
      winner = index;
    }
  }
  const winnerText = spokenFolds[winner];
  return [MATCH_CONTAINED, containing.filter((index) => spokenFolds[index] === winnerText)];
}

// Which of `spokenCount` spoken occurrences the `ordinal`-th of `writtenCount`
// written occurrences plays, in integers, so the server computes the same.
function occurrenceIndex(ordinal, writtenCount, spokenCount) {
  const index = Math.floor((2 * ordinal * spokenCount + writtenCount) / (2 * writtenCount));
  return Math.min(index, spokenCount - 1);
}

/**
 * The audio rule of spec section 3, for every written span of a reply.
 *
 * Returns a list aligned with `writtenSpans` (empty when it is not a list),
 * each entry {match: "exact" | "contained" | "none", spokenIndex: number |
 * null}, where spokenIndex indexes `spokenSpans` as given.
 * @param {*} writtenSpans
 * @param {*} spokenSpans
 * @param {*} learningLang
 * @returns {{match: string, spokenIndex: number|null}[]}
 */
export function audioMatches(writtenSpans, spokenSpans, learningLang) {
  if (!Array.isArray(writtenSpans)) return [];
  const code = learningLanguageCode(learningLang);
  const writtenFolds = learningFolds(writtenSpans, code);
  const spokenFolds = learningFolds(spokenSpans, code);
  const unspaced = code !== null && UNSPACED_LANGUAGE_CODES.includes(code);
  const writtenCounts = new Map();
  writtenFolds.forEach((folded) => {
    if (folded) writtenCounts.set(folded, (writtenCounts.get(folded) || 0) + 1);
  });
  const seen = new Map();
  return writtenFolds.map((folded) => {
    if (!folded) return { match: MATCH_NONE, spokenIndex: null };
    const ordinal = seen.get(folded) || 0;
    seen.set(folded, ordinal + 1);
    const [kind, group] = matchGroup(folded, spokenFolds, unspaced);
    if (!group.length) return { match: MATCH_NONE, spokenIndex: null };
    const chosen = group[occurrenceIndex(ordinal, writtenCounts.get(folded), group.length)];
    return { match: kind, spokenIndex: chosen };
  });
}
